// FormFieldRenderer — ЕДИНЫЙ рендерер любого поля формы (SRP/SSoT):
// кастомные формы (CustomLayoutRenderer), предпросмотр (FormPreview),
// холст дизайнера (FormCanvas), ТЧ-ячейки. Никаких дублей-виджетов.
// Поддерживает все типы: text, numeric, integer, uuid, date, boolean,
// timestamp, enum, reference, file.
import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { CameraCaptureDialog } from '@/components/media/CameraCaptureDialog';
import { captureLocation } from '@/lib/geolocation';
import { getSDK } from '@/store/sdkStore';
import { matchExtensionForField, loadFieldExtensions, OFFER_TEMPLATE_EXTENSION_CODE } from '@/lib/fieldExtensions';
import { AddressField } from '@/components/forms/AddressField';
import { OfferTemplateButton } from '@/components/forms/OfferTemplateButton';
import { offerTemplateSettingsFrom } from '@/lib/offerTemplates';
import { HelpHint } from '@/components/ui/HelpHint';
import { Mic, X } from 'lucide-react';
import { startStt, createVoiceDictationComposer } from '@/lib/stt';
import { useSttAvailability } from '@/lib/useSttAvailability';
import { translate } from '@/lib/i18n';

export interface FieldMeta {
  name: string;
  label?: string;
  data_type: string;          // канонический тип из Type Registry
  is_required?: boolean;
  is_read_only?: boolean;
  ref_table?: string;         // для reference/uuid FK; для enumeration — код перечисления
  options?: EnumOption[];     // для enum/enumeration
  placeholder?: string;
  group_name?: string;
  display_order?: number;
  /** Подсказка реквизита (как в 1С): текст подсказки */
  hint?: string;
  /** Положение подсказки: '' (нет) | 'hover' | 'bottom' | 'button' */
  hint_position?: string;
  /** Вид файлового поля: input (по умолчанию) | label (Надпись) | card (Карточка) */
  viewMode?: string;
  /** Действие по клику (файловое поле): none | download | preview */
  action?: string;
  /** Выводимые поля карточки файла: size | name | uploaded_by | uploaded_at */
  cardFields?: string[];
  /** Голосовой ввод (микрофон у текстового поля): свойство реквизита/поля
   *  формы (по умолчанию выключено; задаётся в FieldForm/FormPropertiesPanel) */
  voiceInput?: boolean;
  /** РАСШИРЕНИЯ поля (элемент макета): [{code:'address'|'voice_input'|..., settings}] */
  extensions?: Array<{ code: string; settings?: Record<string, unknown> }>;
  /** Объект-родитель поля (для default_for правила дополнения: object_type/object_code) */
  parentType?: string;
  parentCode?: string;
}

interface FormFieldRendererProps {
  field: FieldMeta;
  value: unknown;
  onChange: (value: unknown) => void;
  onPick?: () => void;        // для reference полей — открыть пикер
  error?: string;
  disabled?: boolean;
  className?: string;
  /** Для ТЧ: показывать без Label (inline mode) */
  compact?: boolean;
  /** Представление ссылки от СЕРВЕРА ({col}_presentation) — единственный источник отображения */
  displayValue?: string;
  /** ОТОБРАЖЕНИЕ ЗАГОЛОВКА поля (как в 1С): none | top | left | right | bottom | inside */
  labelPosition?: 'none' | 'top' | 'left' | 'right' | 'bottom' | 'inside';
  /** Значения формы целиком (для дополнений, которым нужен контекст ДРУГИХ полей:
   *  напр. «Применение шаблона отклика» читает ссылку на заявку — settings.request_field) */
  formValues?: Record<string, unknown>;
}

/** Определяет UI-тип по каноническому типу */
import {
  resolveUIType, formatNumber, parseDateValue, parseTimestampValue, UuidField, ReferenceField,
  toBool, BooleanField, EnumField, JsonField, HintWrap, type EnumOption,
} from './fieldWidgets';
export type { EnumOption } from './fieldWidgets';
export function FormFieldRenderer({
  field, value, onChange, onPick, error, disabled, className, compact, displayValue, labelPosition, formValues,
}: FormFieldRendererProps) {
  const uiType = resolveUIType(field.data_type, field.ref_table);
  const isDisabled = disabled || field.is_read_only;
  const label = compact ? '' : (field.label || field.name);
  // Имя загруженного файла (реквизит «Файл»): БЕЗУСЛОВНЫЕ хуки (правила React)
  const [fileName, setFileName] = useState('');
  const [fileError, setFileError] = useState('');
  // Сведения о файле (SSoT — GetFileInfo) и данные предпросмотра (карточка/действия)
  const [fileDetail, setFileDetail] = useState<{ fileName: string; fileSize: number; fileType: string; uploadedBy: string; uploadedAt: string } | null>(null);
  const [previewData, setPreviewData] = useState<{ url: string; type: string; name: string } | null>(null);
  // Thumb-превью изображения (карточка файла) — data URL (SSoT — downloadFile)
  const [thumbUrl, setThumbUrl] = useState('');
  // Универсальный захват с камеры (реквизит «Файл»)
  const [cameraOpen, setCameraOpen] = useState(false);

  // Для реквизита «Файл» в видах Надпись/Карточка — сведения с сервера (SSoT)
  useEffect(() => {
    if (uiType !== 'file' || field.viewMode === 'input' || field.viewMode === undefined) return;
    const fileId = String(value ?? '');
    if (!fileId) { setFileDetail(null); setThumbUrl(''); return; }
    let cancelled = false;
    getSDK().files.getFileInfo({ id: fileId })
      .then(async (r: any) => {
        if (cancelled) return;
        const detail = { fileName: r.fileName || '', fileSize: Number(r.fileSize || 0), fileType: r.fileType || '', uploadedBy: r.uploadedBy || '', uploadedAt: r.uploadedAt || '' };
        setFileDetail(detail);
        // Изображение → thumb для карточки (только изображения)
        const isImg = (detail.fileType || '').startsWith('image/');
        if (isImg) {
          try {
            const resp = await getSDK().files.downloadFile({ id: fileId });
            const blob = new Blob([resp.data as any], { type: detail.fileType || 'application/octet-stream' });
            if (!cancelled) setThumbUrl(URL.createObjectURL(blob));
          } catch { /* thumb недоступен — карточка без превью */ }
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setFileError(err?.message || translate('form.file_info_error'));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiType, field.viewMode, value]);
  // Реестр дополнений платформы: загрузка + пере-рендер (вид «Дополнение адреса»)
  const [extReady, setExtReady] = useState(false);
  useEffect(() => {
    void loadFieldExtensions().then(() => setExtReady(true)).catch(() => setExtReady(true));
  }, []);
  const renderInput = () => {
    switch (uiType) {
      case 'reference':
        // ДОПОЛНЕНИЕ ПЛАТФОРМЫ: реквизит «СправочникСсылка.Адреса» — вид по
        // умолчанию «Дополнение адреса» (реестр default_for; SSoT — сервер)
        if (extReady && matchExtensionForField(field as any)) {
          return (
            <AddressField
              value={value}
              displayValue={displayValue}
              onChange={onChange}
              disabled={isDisabled}
              placeholder={field.placeholder}
            />
          );
        }
        return (
          <ReferenceField
            value={value}
            displayValue={displayValue}
            onChange={onChange}
            onPick={onPick}
            disabled={isDisabled}
            placeholder={field.placeholder}
          />
        );

      case 'boolean':
        return <BooleanField value={value} onChange={onChange} disabled={isDisabled} />;

      case 'enum':
        return <EnumField value={value} options={field.options} onChange={onChange} disabled={isDisabled} />;

      case 'date': {
        // Как в 1С: поле «Дата» документа содержит ВРЕМЯ — если значение с временем,
        // рендерим datetime-local (дата + время), иначе просто дату
        const raw = String(value ?? '');
        const hasTime = raw.includes('T') || raw.includes(':');
        if (hasTime) {
          return (
            <Input
              type="datetime-local"
              value={parseTimestampValue(value)}
              onChange={(e) => onChange(e.target.value)}
              disabled={isDisabled}
              className="h-8 text-sm"
            />
          );
        }
        return (
          <Input
            type="date"
            value={parseDateValue(value)}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled}
            className="h-8 text-sm"
          />
        );
      }

      case 'timestamp':
        return (
          <Input
            type="datetime-local"
            value={parseTimestampValue(value)}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled}
            className="h-8 text-sm"
          />
        );

      case 'file': {
        // Реквизит «Файл» (как в 1С). Виды (viewMode):
        //   input — поле + кнопки (по умолчанию);
        //   label — Надпись (имя файла; клик — действие);
        //   card  — Карточка: thumb-превью (изображения) + выводимые поля.
        // Действие по клику (action): none | download | preview (SSoT — GetFileInfo).
        const fileId = String(value ?? '');
        const viewMode = field.viewMode || 'input';
        const action = field.action || 'none';
        const upload = async (file: File) => {
          try {
            // Координаты — «всегда при возможности» (единый captureLocation);
            // недоступность НЕ блокирует загрузку, но видима (нет тихих фоллбэков)
            let geoLat = 0; let geoLon = 0;
            try {
              const p = await captureLocation();
              geoLat = p.lat; geoLon = p.lon;
            } catch (err: any) {
              setFileError(err?.message || translate('form.geo_unavailable'));
            }
            const buf = await file.arrayBuffer();
            const bytes = new Uint8Array(buf);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            const info = await getSDK().files.uploadFile({
              data: bytes as any,
              fileName: file.name,
              fileType: file.type || 'application/octet-stream',
              ownerId: '',
              geoLat,
              geoLon,
            });
            setFileName(String((info as any)?.fileName || file.name));
            setFileError('');
            onChange(info.id);
          } catch (err: any) {
            setFileError(err?.message || translate('form.file_upload_error'));
          }
        };
        const download = async () => {
          if (!fileId) return;
          try {
            const resp = await getSDK().files.downloadFile({ id: fileId });
            const blob = new Blob([resp.data as any], { type: resp.fileType || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = resp.fileName || 'file';
            a.click();
            URL.revokeObjectURL(url);
          } catch (err: any) {
            setFileError(err?.message || translate('form.file_download_error'));
          }
        };

        // Сведения о файле загружены хуком компонента (label/card виды)
        const isImage = (fileDetail?.fileType || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(fileDetail?.fileName || '');
        const openPreview = async () => {
          if (!fileId) return;
          try {
            const resp = await getSDK().files.downloadFile({ id: fileId });
            const blob = new Blob([resp.data as any], { type: resp.fileType || 'application/octet-stream' });
            const url = URL.createObjectURL(blob);
            setPreviewData({ url, type: resp.fileType || '', name: resp.fileName || 'file' });
          } catch (err: any) {
            setFileError(err?.message || translate('form.file_preview_error'));
          }
        };
        const handleClick = () => {
          if (!fileId || action === 'none' || isDisabled) return;
          if (action === 'download') void download();
          else void openPreview();
        };
        const fmtSize = (n: number) => n >= 1048576 ? `${(n / 1048576).toFixed(1)} МБ` : n >= 1024 ? `${(n / 1024).toFixed(1)} КБ` : `${n} Б`;
        const fmtDate = (s: string) => (s ? new Date(s).toLocaleString('ru-RU') : '');
        // Универсальный захват с камеры: снимок → тот же upload (значение = id файла)
        const cameraModal = (
          <CameraCaptureDialog
            open={cameraOpen}
            onClose={() => setCameraOpen(false)}
            onCaptured={({ file }) => { setCameraOpen(false); void upload(file); }}
          />
        );
        const cameraButton = (
          <Button size="sm" variant="outline" className="h-8 px-2 text-xs shrink-0" onClick={() => setCameraOpen(true)} disabled={isDisabled} title={translate('form.camera')}>
            Камера
          </Button>
        );
        // Модалка предпросмотра (action=preview): изображения — картинка, PDF — встроенный
        const previewModal = previewData && (
          <UniversalWindow open onClose={() => { setPreviewData(null); }} title={previewData.name} blocking width="max-w-3xl">
            <div className="p-3 flex items-center justify-center min-h-[240px]">
              {previewData.type === 'application/pdf' || previewData.name.toLowerCase().endsWith('.pdf') ? (
                <iframe src={previewData.url} title={previewData.name} className="w-full h-[60vh] rounded border" />
              ) : (
                <img src={previewData.url} alt={previewData.name} className="max-w-full max-h-[70vh] rounded" />
              )}
            </div>
          </UniversalWindow>
        );

        // ВИД «Надпись»: имя файла; клик — действие (скачать/предпросмотр)
        if (viewMode === 'label') {
          return (
            <div className="space-y-1">
              <button
                type="button"
                disabled={isDisabled}
                onClick={handleClick}
                className={`text-sm truncate max-w-full ${action !== 'none' && fileId ? 'text-primary underline underline-offset-2 cursor-pointer' : ''} ${isDisabled ? 'opacity-60' : ''}`}
                title={fileDetail?.fileName || (fileId ? translate('form.file_uploaded') : '')}
              >
                {fileDetail?.fileName || fileName || (fileId ? translate('form.file_uploaded') : '—')}
              </button>
              {!isDisabled && (
                <label className="text-xs border rounded px-2 py-1 cursor-pointer hover:bg-muted/30 inline-flex items-center gap-1 w-fit">
                  Загрузить файл
                  <input type="file" className="hidden" disabled={isDisabled} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }} />
                </label>
              )}
              {!isDisabled && cameraButton}
              {fileError && <div className="text-[10px] text-destructive">{fileError}</div>}
              {previewModal}
              {cameraModal}
            </div>
          );
        }

        // ВИД «Карточка»: thumb-превью + выводимые поля (cardFields)
        if (viewMode === 'card') {
          const fields = field.cardFields && field.cardFields.length > 0 ? field.cardFields : ['name', 'size'];
          return (
            <div className="space-y-1">
              {fileId ? (
                <div
                  className={`border rounded-md p-2 space-y-1 bg-background ${action !== 'none' ? 'cursor-pointer hover:bg-accent/30' : ''}`}
                  onClick={handleClick}
                  title={action !== 'none' ? (action === 'download' ? translate('form.download') : translate('form.preview')) : ''}
                >
                  {isImage && thumbUrl && (
                    <div className="h-20 w-20 rounded border overflow-hidden bg-muted/30">
                      <img src={thumbUrl} alt={fileDetail?.fileName || 'thumb'} className="h-full w-full object-cover" />
                    </div>
                  )}
                  {fields.includes('name') && <div className="text-xs font-medium truncate">{fileDetail?.fileName || fileName || 'файл загружен'}</div>}
                  {fields.includes('size') && fileDetail && <div className="text-[10px] text-muted-foreground">Размер: {fmtSize(fileDetail.fileSize)}</div>}
                  {fields.includes('uploaded_by') && fileDetail?.uploadedBy && <div className="text-[10px] text-muted-foreground">Загрузил: {fileDetail.uploadedBy}</div>}
                  {fields.includes('uploaded_at') && fileDetail?.uploadedAt && <div className="text-[10px] text-muted-foreground">Загрузка: {fmtDate(fileDetail.uploadedAt)}</div>}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">{translate('form.no_file')}</div>
              )}
              {!isDisabled && (
                <label className="text-xs border rounded px-2 py-1 cursor-pointer hover:bg-muted/30 inline-flex items-center gap-1 w-fit">
                  Загрузить файл
                  <input type="file" className="hidden" disabled={isDisabled} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }} />
                </label>
              )}
              {!isDisabled && cameraButton}
              {fileError && <div className="text-[10px] text-destructive">{fileError}</div>}
              {previewModal}
              {cameraModal}
            </div>
          );
        }

        // ВИД «Поле ввода» (по умолчанию)
        return (
          <div className="space-y-1">
            <div className="flex items-center gap-1">
              <Input
                value={fileName || (fileId ? translate('form.file_uploaded') : '')}
                placeholder={translate('form.no_file')}
                readOnly
                disabled={isDisabled}
                className="h-8 text-sm flex-1 min-w-0"
              />
              <label className="text-xs border rounded px-2 py-1.5 cursor-pointer hover:bg-muted/30 inline-flex items-center gap-1 shrink-0">
                Загрузить файл
                <input type="file" className="hidden" disabled={isDisabled} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void upload(f); }} />
              </label>
              {!isDisabled && cameraButton}
              {fileId && (
                <Button size="sm" variant="outline" className="h-8 px-2 text-xs shrink-0" onClick={() => void download()} disabled={isDisabled}>
                  Скачать
                </Button>
              )}
              {onPick && (
                <Button size="sm" variant="outline" className="h-8 px-2 text-xs shrink-0" onClick={onPick} disabled={isDisabled} title={translate('form.choose_file')}>
                  ...
                </Button>
              )}
            </div>
              {fileError && <div className="text-[10px] text-destructive">{fileError}</div>}
              {previewModal}
              {cameraModal}
            </div>
          );
      }

      case 'number':
      case 'integer':
        return (
          <Input
            type="number"
            value={String(value ?? '')}
            onChange={(e) => {
              const v = e.target.value;
              if (uiType === 'integer') onChange(v === '' ? '' : parseInt(v, 10) || '');
              else onChange(v === '' ? '' : parseFloat(v) || '');
            }}
            disabled={isDisabled}
            className="h-8 text-sm text-right font-mono"
          />
        );

      case 'uuid':
        return <UuidField value={value} onChange={onChange} disabled={isDisabled} />;

      case 'json':
        return <JsonField value={value} onChange={onChange} disabled={isDisabled} />;

      case 'text': {
        // РАСШИРЕНИЯ ПОЛЯ (элемент макета) + default_for (реестр дополнений):
        // 1) «Дополнение адреса» (address) — явная пометка на элементе формы
        //    ИЛИ default_for (ссылка на «Адреса») → адресное поле (ввод с
        //    подбором из справочника + создание при сохранении);
        // 2) иначе — обычное текстовое поле с ПОВЕДЕНЧЕСКИМИ расширениями:
        //    голосовой ввод (voice_input/STT) и «✕» (очистка) — по умолчанию.
        const hasExt = (code: string) =>
          (field.extensions || []).some((x) => x.code === code) ||
          (field.voiceInput === true && code === 'voice_input');
        const isAddress =
          hasExt('address') || (extReady && matchExtensionForField(field as any)?.code === 'address');
        if (isAddress) {
          return (
            <AddressField
              mode="text"
              value={value}
              onChange={onChange}
              disabled={isDisabled}
              placeholder={field.placeholder}
              voiceInput={field.voiceInput === true}
            />
          );
        }
        // 3) «Применение шаблона отклика» (offer_template): пометка на элементе
        //    ИЛИ встроенный вид по default_for (поле «Текст отклика» документа
        //    mkt_responses) → выбор АКТИВНОГО шаблона с подстановкой {{Поле}}
        //    из связанной заявки (lib/offerTemplates.ts, OfferTemplateButton.tsx).
        const offerTemplateBtn =
          (hasExt(OFFER_TEMPLATE_EXTENSION_CODE) ||
            (extReady && matchExtensionForField(field as any)?.code === OFFER_TEMPLATE_EXTENSION_CODE)) && !isDisabled ? (
            <OfferTemplateButton value={String(value ?? '')} onApply={(t) => onChange(t)} formValues={formValues} settings={offerTemplateSettingsFrom(field.extensions)} />
          ) : null;
        // ВИД «Многострочное» (viewMode='textarea', свойство элемента макета):
        // textarea с переносами строк (многострочные описания). ЕДИНЫЙ рендер —
        // кастомные формы, предпросмотр, автоформы (при явном viewMode).
        // Голосовой ввод (voice_input) доступен и здесь (у заявок — по умолчанию).
        if (field.viewMode === 'textarea') {
          const showMicArea = !isDisabled && hasExt('voice_input');
          return (
            <div className="flex items-start gap-1">
              <textarea
                value={String(value ?? '')}
                onChange={(e) => onChange(e.target.value)}
                disabled={isDisabled}
                placeholder={field.placeholder}
                className="w-full flex-1 min-w-0 rounded-md border bg-input px-3 py-2 text-sm h-20 resize-y"
              />
              {showMicArea && <VoiceInputButton value={String(value ?? '')} onValue={(full) => onChange(full)} />}
              {offerTemplateBtn}
            </div>
          );
        }
        const showMic = !isDisabled && hasExt('voice_input');
        return (
          <div className="flex items-center gap-1">
            <div className="relative flex-1 min-w-0">
              <Input
                value={String(value ?? '')}
                onChange={(e) => onChange(e.target.value)}
                disabled={isDisabled}
                className="h-8 text-sm w-full pr-6"
                placeholder={field.placeholder}
              />
              {!isDisabled && String(value ?? '') !== '' && (
                <button
                  type="button"
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive"
                  title={translate('common.clear')}
                  onClick={() => onChange('')}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
            {showMic && <VoiceInputButton value={String(value ?? '')} onValue={(full) => onChange(full)} />}
            {offerTemplateBtn}
          </div>
        );
      }

      default: // 'text'
        return (
          <Input
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
            disabled={isDisabled}
            className="h-8 text-sm"
            placeholder={field.placeholder}
          />
        );
    }
  };

  const renderInputWithHint = () => <HintWrap field={field}>{renderInput()}</HintWrap>;

  if (compact) {
    return (
      <div className={`${className || ''}`}>
        {renderInputWithHint()}
        {error && <div className="text-[10px] text-destructive mt-0.5">{error}</div>}
      </div>
    );
  }

  // ОТОБРАЖЕНИЕ ЗАГОЛОВКА (как в 1С): none/top/left/right/bottom/inside.
  // Для 'inside' заголовок становится плейсхолдером поля (только текстовые типы).
  const pos = labelPosition || 'top';
  const labelNode = label && (
    <Label className={`text-xs ${pos === 'right' || pos === 'left' ? 'shrink-0 pt-2' : ''}`}>
      {label}
      {field.is_required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );

  if (pos === 'none') {
    return (
      <div className={`${className || ''}`}>
        {renderInputWithHint()}
        {error && <div className="text-[10px] text-destructive mt-0.5">{error}</div>}
      </div>
    );
  }

  if (pos === 'left' || pos === 'right') {
    return (
      <div className={`flex items-center gap-2 ${pos === 'right' ? 'flex-row-reverse' : ''} ${className || ''}`}>
        {labelNode}
        <div className="flex-1 min-w-0">
          {renderInputWithHint()}
          {error && <div className="text-[10px] text-destructive mt-0.5">{error}</div>}
        </div>
      </div>
    );
  }

  if (pos === 'bottom') {
    return (
      <div className={`space-y-0 ${className || ''}`}>
        {renderInputWithHint()}
        {labelNode}
        {error && <div className="text-[10px] text-destructive mt-0.5">{error}</div>}
      </div>
    );
  }

  if (pos === 'inside' && ['text', 'number', 'integer', 'uuid'].includes(uiType)) {
    return (
      <div className={`space-y-1 ${className || ''}`}>
        <Input
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          disabled={isDisabled}
          className="h-8 text-sm"
          placeholder={label}
        />
        {error && <div className="text-[10px] text-destructive">{error}</div>}
      </div>
    );
  }

  return (
    <div className={`space-y-1 ${className || ''}`}>
      {labelNode}
      {renderInputWithHint()}
      {error && <div className="text-[10px] text-destructive">{error}</div>}
    </div>
  );
}

// Re-export FieldMeta for external use
export type { FieldMeta as FormFieldMeta };

// ============================================================================
// VoiceInputButton — микрофон голосового ввода у текстовых полей (реквизит/
// поле формы с флагом «Голосовой ввод»). СЕМАНТИКА (SSoT, lib/stt): поле ДО
// диктовки = исходный текст (value фиксируется при старте); ВО ВРЕМЯ = исходный
// + зафиксированные финалы + ТЕКУЩИЙ interim (interim ПЕРЕЗАПИСЫВАЕТСЯ, а не
// дописывается — регресс «ииваИванов…»); onValue получает ПОЛНОЕ значение
// поля (вызывающий ЗАМЕНЯЕТ значение, не дописывает). Недоступность движка —
// кнопки НЕТ вовсе (никаких неактивных заглушек).
// ============================================================================
export function VoiceInputButton({ value, onValue, className }: {
  /** Текущее значение поля — фиксируется как исходное при старте диктовки */
  value: string;
  /** ПОЛНОЕ значение поля (исходное + финалы + interim) — заменяет содержимое */
  onValue: (fullText: string) => void;
  className?: string;
}) {
  const [listening, setListening] = useState(false);
  const sessionRef = useRef<{ stop: () => void } | null>(null);
  const composerRef = useRef<ReturnType<typeof createVoiceDictationComposer> | null>(null);
  const [tip, setTip] = useState('');
  // ЕДИНАЯ проверка доступности (SSoT — useSttAvailability: permissionCenter +
  // микрофон + SpeechRecognition); запрещённый микрофон/нет движка → кнопки НЕТ.
  const stt = useSttAvailability();

  if (!stt.canTranscribe) return null;

  const toggle = async () => {
    if (listening) {
      sessionRef.current?.stop();
      sessionRef.current = null;
      composerRef.current = null;
      setListening(false);
      return;
    }
    try {
      // Исходный текст поля — база композитора; interim/финалы пересчитываются
      // от неё (поле = исходный + зафиксировано + текущий interim).
      composerRef.current = createVoiceDictationComposer(value, onValue);
      setListening(true);
      const session = await startStt({
        continuous: true,
        onText: (t, final) => composerRef.current?.push(t, final),
        onError: () => { setTip(translate('form.voice_error')); setTimeout(() => setTip(''), 2500); },
      });
      sessionRef.current = session;
    } catch (e: any) {
      setListening(false);
      setTip(e?.message || translate('form.voice_start_error'));
      setTimeout(() => setTip(''), 2500);
    }
  };

  return (
    <span className={`relative inline-flex items-center ${className || ''}`}>
      <button
        type="button"
        className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${listening ? 'bg-destructive text-white animate-pulse' : 'hover:bg-muted/40'}`}
        title={listening ? translate('common.stop_listening') : translate('form.voice_input')}
        onClick={() => void toggle()}
      >
        <Mic className="h-3.5 w-3.5" />
      </button>
      {tip && (
        <span className="absolute right-0 top-full mt-1 z-30 whitespace-nowrap rounded bg-popover border px-2 py-1 text-[10px] text-popover-foreground shadow">
          {tip}
        </span>
      )}
    </span>
  );
}
